import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, Trash2, Plus, Check, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { cn } from '../../lib/utils';
import {
  primaryText,
  secondaryText,
  smallTextSize,
} from '../../styling/stylingHelper';
import { SortableList } from '../common/SortableList';
import {
  RecipeComponent,
  RecipeIngredient,
  RecipeStep,
  getTranslatedText,
  TranslatedText,
  SupportedLanguage,
  ALL_LANGUAGES,
  translateTexts,
} from '../../services/recipes/recipesService';
import LanguageTabBar from './LanguageTabBar';

// Helper to create a translation object with text in a specific language
const toTranslatedText = (text: string, lang: SupportedLanguage = 'en'): TranslatedText => {
  const t: TranslatedText = { en: '' };
  t[lang] = text;
  return t;
};

// Generate a simple UUID for new items (browser-compatible)
const generateId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

interface SortableStepRowProps {
  step: RecipeStep;
  index: number;
  lang: SupportedLanguage;
  t: (key: string) => string;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onRemove: () => void;
}

const SortableStepRow = ({ step, index, lang, t, onTitleChange, onDescriptionChange, onRemove }: SortableStepRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id || `step-${index}`,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg"
    >
      <button
        {...attributes}
        {...listeners}
        className="touch-none cursor-grab active:cursor-grabbing mt-0.5 flex-shrink-0"
        aria-label="Drag to reorder"
        type="button"
      >
        <GripVertical className={cn('w-4 h-4', secondaryText)} />
      </button>
      <span className={cn(smallTextSize, 'font-semibold', secondaryText, 'flex-shrink-0 mt-0.5')}>
        {index + 1}.
      </span>
      <div className="flex-1 space-y-1">
        <input
          type="text"
          value={step.titleTranslations ? getTranslatedText(step.titleTranslations, lang) : ''}
          onChange={(e) => onTitleChange(e.target.value)}
          className={cn(
            'w-full bg-transparent dark:bg-transparent border-b border-transparent focus:border-zinc-400 dark:focus:border-zinc-500 outline-none font-semibold',
            smallTextSize, primaryText
          )}
          placeholder={t('recipes.stepTitle') + ' (' + t('common.optional') + ')'}
        />
        <textarea
          value={getTranslatedText(step.descriptionTranslations, lang)}
          onChange={(e) => onDescriptionChange(e.target.value)}
          className={cn(
            'w-full bg-transparent dark:bg-transparent border-b border-transparent focus:border-zinc-400 dark:focus:border-zinc-500 outline-none resize-none',
            smallTextSize, secondaryText
          )}
          placeholder={t('recipes.stepDescription')}
          rows={2}
        />
      </div>
      <button
        onClick={onRemove}
        className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition flex-shrink-0"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

interface ComponentEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  component?: RecipeComponent;
  onSave: (component: RecipeComponent, imageFiles: File[]) => void;
  hasRecipeAutomationAccess?: boolean;
}

export default function ComponentEditorModal({
  isOpen,
  onClose,
  mode,
  component,
  onSave,
  hasRecipeAutomationAccess = false,
}: ComponentEditorModalProps) {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultLang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>(defaultLang);
  const [isTranslating, setIsTranslating] = useState(false);

  const [nameTranslations, setNameTranslations] = useState<TranslatedText>({ en: '' });
  const [amountTranslations, setAmountTranslations] = useState<TranslatedText | undefined>(undefined);
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [steps, setSteps] = useState<RecipeStep[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Ingredient form state
  const [ingredientName, setIngredientName] = useState('');
  const [ingredientAmount, setIngredientAmount] = useState('');

  // Step form state
  const [stepTitle, setStepTitle] = useState('');
  const [stepDescription, setStepDescription] = useState('');

  // Bulk text input state (for automation users)
  const [bulkIngredientsText, setBulkIngredientsText] = useState('');
  const [bulkStepsText, setBulkStepsText] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && component) {
        setNameTranslations(component.nameTranslations || { en: '' });
        setAmountTranslations(component.amountTranslations || undefined);
        setIngredients(component.ingredients || []);
        // Sort steps by position when loading
        const sortedSteps = [...(component.steps || [])].sort((a, b) => {
          const posA = a.position !== undefined ? a.position : Infinity;
          const posB = b.position !== undefined ? b.position : Infinity;
          return posA - posB;
        });
        setSteps(sortedSteps);
        setExistingImages(component.images || []);
        setImageFiles([]);
      } else {
        setNameTranslations({ en: '' });
        setAmountTranslations(undefined);
        setIngredients([]);
        setSteps([]);
        setExistingImages([]);
        setImageFiles([]);
      }
      setActiveLanguage(defaultLang);
      setError(null);
      resetIngredientForm();
      resetStepForm();
    }
  }, [isOpen, mode, component, defaultLang]);

  const resetIngredientForm = () => {
    setIngredientName('');
    setIngredientAmount('');
  };

  const resetStepForm = () => {
    setStepTitle('');
    setStepDescription('');
  };

  // ---- Language helpers ----

  const hasContent = {
    en: !!(nameTranslations.en || amountTranslations?.en || ingredients.some(i => getTranslatedText(i.nameTranslations, 'en')) || steps.some(s => getTranslatedText(s.descriptionTranslations, 'en'))),
    es: !!(nameTranslations.es || amountTranslations?.es || ingredients.some(i => i.nameTranslations.es) || steps.some(s => s.descriptionTranslations.es)),
    nl: !!(nameTranslations.nl || amountTranslations?.nl || ingredients.some(i => i.nameTranslations.nl) || steps.some(s => s.descriptionTranslations.nl)),
  };

  const handleTranslate = async () => {
    const src = activeLanguage;
    const targets = ALL_LANGUAGES.filter(l => l !== src);

    // Gather all texts that have content in the source language
    const texts: { key: string; value: string }[] = [];

    const srcName = nameTranslations[src];
    if (srcName) texts.push({ key: 'comp_name', value: srcName });
    if (amountTranslations) {
      const srcAmount = getTranslatedText(amountTranslations, src);
      if (srcAmount) texts.push({ key: 'comp_amount', value: srcAmount });
    }

    ingredients.forEach((ing, i) => {
      const v = getTranslatedText(ing.nameTranslations, src);
      if (v) texts.push({ key: `ing_name_${i}`, value: v });
      if (ing.amountTranslations) {
        const a = getTranslatedText(ing.amountTranslations, src);
        if (a) texts.push({ key: `ing_amt_${i}`, value: a });
      }
    });

    steps.forEach((step, i) => {
      if (step.titleTranslations) {
        const v = getTranslatedText(step.titleTranslations, src);
        if (v) texts.push({ key: `step_title_${i}`, value: v });
      }
      const d = getTranslatedText(step.descriptionTranslations, src);
      if (d) texts.push({ key: `step_desc_${i}`, value: d });
    });

    if (texts.length === 0) return;

    setIsTranslating(true);
    setError(null);
    try {
      const { translations } = await translateTexts(texts, src, targets);

      // Apply translations
      const get = (key: string, lang: SupportedLanguage) => translations[key]?.[lang] || '';

      // Component name
      if (translations['comp_name']) {
        const updated = { ...nameTranslations };
        for (const tl of targets) updated[tl] = get('comp_name', tl);
        setNameTranslations(updated);
      }

      // Component amount
      if (translations['comp_amount'] && amountTranslations) {
        const updated = { ...amountTranslations };
        for (const tl of targets) updated[tl] = get('comp_amount', tl);
        setAmountTranslations(updated);
      }

      // Ingredients
      setIngredients(prev => prev.map((ing, i) => {
        const newIng = { ...ing };
        if (translations[`ing_name_${i}`]) {
          const nt = { ...ing.nameTranslations };
          for (const tl of targets) nt[tl] = get(`ing_name_${i}`, tl);
          newIng.nameTranslations = nt;
        }
        if (translations[`ing_amt_${i}`] && ing.amountTranslations) {
          const at = { ...ing.amountTranslations };
          for (const tl of targets) at[tl] = get(`ing_amt_${i}`, tl);
          newIng.amountTranslations = at;
        }
        return newIng;
      }));

      // Steps
      setSteps(prev => prev.map((step, i) => {
        const newStep = { ...step };
        if (translations[`step_title_${i}`] && step.titleTranslations) {
          const tt = { ...step.titleTranslations };
          for (const tl of targets) tt[tl] = get(`step_title_${i}`, tl);
          newStep.titleTranslations = tt;
        }
        if (translations[`step_desc_${i}`]) {
          const dt = { ...step.descriptionTranslations };
          for (const tl of targets) dt[tl] = get(`step_desc_${i}`, tl);
          newStep.descriptionTranslations = dt;
        }
        return newStep;
      }));
    } catch (err: any) {
      console.error('Translation error:', err);
      setError(err.message || 'Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  /**
   * Parse bulk ingredients text in format:
   * name: amount
   * name: amount
   */
  const parseBulkIngredients = (text: string): RecipeIngredient[] => {
    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const parsedIngredients: RecipeIngredient[] = [];

    for (const line of lines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1) {
        const ingName = line.substring(0, colonIndex).trim();
        const amount = line.substring(colonIndex + 1).trim();
        if (ingName) {
          parsedIngredients.push({
            id: generateId(),
            nameTranslations: toTranslatedText(ingName, activeLanguage),
            amountTranslations: amount ? toTranslatedText(amount, activeLanguage) : undefined,
          });
        }
      } else {
        if (line) {
          parsedIngredients.push({
            id: generateId(),
            nameTranslations: toTranslatedText(line, activeLanguage),
            amountTranslations: undefined,
          });
        }
      }
    }

    return parsedIngredients;
  };

  /**
   * Parse bulk steps text
   */
  const parseBulkSteps = (text: string): RecipeStep[] => {
    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const parsedSteps: RecipeStep[] = [];

    for (let i = 0; i < lines.length; i++) {
      const cleaned = lines[i].replace(/^[-·•]\s*/, '').trim();
      if (cleaned) {
        parsedSteps.push({
          id: generateId(),
          descriptionTranslations: toTranslatedText(cleaned, activeLanguage),
          position: steps.length + i,
        });
      }
    }

    return parsedSteps;
  };

  const handleParseBulkIngredients = () => {
    if (!bulkIngredientsText.trim()) return;

    const parsed = parseBulkIngredients(bulkIngredientsText);
    if (parsed.length > 0) {
      setIngredients([...ingredients, ...parsed]);
      setBulkIngredientsText('');
    }
  };

  const handleParseBulkSteps = () => {
    if (!bulkStepsText.trim()) return;

    const parsed = parseBulkSteps(bulkStepsText);
    if (parsed.length > 0) {
      setSteps([...steps, ...parsed]);
      setBulkStepsText('');
    }
  };

  const handleAddIngredient = () => {
    if (ingredientName.trim()) {
      setIngredients([
        ...ingredients,
        {
          id: generateId(),
          nameTranslations: toTranslatedText(ingredientName.trim(), activeLanguage),
          amountTranslations: ingredientAmount.trim() ? toTranslatedText(ingredientAmount.trim(), activeLanguage) : undefined,
        },
      ]);
      resetIngredientForm();
    }
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleAddStep = () => {
    if (stepDescription.trim()) {
      setSteps([
        ...steps,
        {
          id: generateId(),
          titleTranslations: stepTitle.trim() ? toTranslatedText(stepTitle.trim(), activeLanguage) : undefined,
          descriptionTranslations: toTranslatedText(stepDescription.trim(), activeLanguage),
          position: steps.length,
        },
      ]);
      resetStepForm();
    }
  };

  const handleRemoveStep = (index: number) => {
    const updatedSteps = steps
      .filter((_, i) => i !== index)
      .map((step, newIndex) => ({
        ...step,
        position: newIndex,
      }));
    setSteps(updatedSteps);
  };

  const handleStepsReorder = (reordered: RecipeStep[]) => {
    setSteps(reordered.map((step, index) => ({ ...step, position: index })));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setImageFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const handleRemoveNewImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index: number) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper to update a specific language in a TranslatedText for an ingredient
  const updateIngredientField = (
    index: number,
    field: 'nameTranslations' | 'amountTranslations',
    value: string
  ) => {
    setIngredients(prev => prev.map((ing, i) => {
      if (i !== index) return ing;
      if (field === 'amountTranslations') {
        if (!value && !ing.amountTranslations) return ing;
        const existing = ing.amountTranslations || { en: '' };
        return { ...ing, amountTranslations: { ...existing, [activeLanguage]: value } };
      }
      return { ...ing, nameTranslations: { ...ing.nameTranslations, [activeLanguage]: value } };
    }));
  };

  // Helper to update a specific language in a TranslatedText for a step
  const updateStepField = (
    index: number,
    field: 'titleTranslations' | 'descriptionTranslations',
    value: string
  ) => {
    setSteps(prev => prev.map((step, i) => {
      if (i !== index) return step;
      if (field === 'titleTranslations') {
        const existing = step.titleTranslations || { en: '' };
        return { ...step, titleTranslations: { ...existing, [activeLanguage]: value } };
      }
      return { ...step, descriptionTranslations: { ...step.descriptionTranslations, [activeLanguage]: value } };
    }));
  };

  const handleSave = () => {
    const currentName = nameTranslations[activeLanguage] || nameTranslations.en || '';
    if (!currentName.trim()) {
      setError(t('recipes.componentNameRequired') || 'Component name is required');
      return;
    }

    // Ensure en is populated (use active language as fallback)
    const finalNameTranslations = { ...nameTranslations };
    if (!finalNameTranslations.en) {
      finalNameTranslations.en = nameTranslations[activeLanguage] || '';
    }

    // Component amount: ensure en fallback, strip if empty
    let finalAmountTranslations: TranslatedText | undefined = undefined;
    if (amountTranslations) {
      const at = { ...amountTranslations };
      if (!at.en) at.en = getTranslatedText(amountTranslations, activeLanguage);
      // Only keep if there's actual content
      if (at.en || at.es || at.nl) {
        finalAmountTranslations = at;
      }
    }

    // Same for ingredients and steps: ensure en fallback
    const finalIngredients = ingredients.map(ing => {
      const nt = { ...ing.nameTranslations };
      if (!nt.en) nt.en = getTranslatedText(ing.nameTranslations, activeLanguage);
      const result: RecipeIngredient = { ...ing, nameTranslations: nt };
      if (ing.amountTranslations) {
        const at = { ...ing.amountTranslations };
        if (!at.en) at.en = getTranslatedText(ing.amountTranslations, activeLanguage);
        result.amountTranslations = at;
      }
      return result;
    });

    const finalSteps = steps.map((step, index) => {
      const dt = { ...step.descriptionTranslations };
      if (!dt.en) dt.en = getTranslatedText(step.descriptionTranslations, activeLanguage);
      const result: RecipeStep = { ...step, descriptionTranslations: dt, position: step.position !== undefined ? step.position : index };
      if (step.titleTranslations) {
        const tt = { ...step.titleTranslations };
        if (!tt.en) tt.en = getTranslatedText(step.titleTranslations, activeLanguage);
        result.titleTranslations = tt;
      }
      return result;
    });

    const updatedComponent: RecipeComponent = {
      id: component?.id || generateId(),
      nameTranslations: finalNameTranslations,
      amountTranslations: finalAmountTranslations,
      images: existingImages,
      ingredients: finalIngredients,
      steps: finalSteps,
      notes: component?.notes || [],
      position: component?.position,
    };

    onSave(updatedComponent, imageFiles);
    onClose();
  };

  if (!isOpen) return null;

  const lang = activeLanguage;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-4xl border border-zinc-200 dark:border-white/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('recipes.createComponent') : t('recipes.editComponent')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Language Tab Bar */}
          <LanguageTabBar
            activeLanguage={activeLanguage}
            onChangeLanguage={setActiveLanguage}
            onTranslate={handleTranslate}
            isTranslating={isTranslating}
            hasContent={hasContent}
          />

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Component Name */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.componentName')} *
              </label>
              <input
                type="text"
                value={nameTranslations[lang] || ''}
                onChange={(e) => setNameTranslations({ ...nameTranslations, [lang]: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark"
                placeholder={t('recipes.enterComponentName')}
              />
            </div>

            {/* Component Amount (optional) */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.amount')} ({t('common.optional')})
              </label>
              <input
                type="text"
                value={amountTranslations ? (amountTranslations[lang] || '') : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val && !amountTranslations) return;
                  const existing = amountTranslations || { en: '' };
                  setAmountTranslations({ ...existing, [lang]: val });
                }}
                className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark"
                placeholder={t('recipes.enterComponentAmount') || 'e.g. 3 portions, 400ml'}
              />
            </div>

            {/* Images */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.images')}
              </label>

              {/* Existing Images */}
              {existingImages.length > 0 && (
                <div className="mb-3">
                  <p className={cn('text-xs', secondaryText, 'mb-2')}>Current images:</p>
                  <div className="flex gap-2 flex-wrap">
                    {existingImages.map((url, index) => (
                      <div key={index} className="relative">
                        <img
                          src={url}
                          alt={`Component ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleRemoveExistingImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 dark:bg-red-600 text-white dark:text-white rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Images Preview */}
              {imageFiles.length > 0 && (
                <div className="mb-3">
                  <p className={cn('text-xs', secondaryText, 'mb-2')}>New images:</p>
                  <div className="flex gap-2 flex-wrap">
                    {imageFiles.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`New ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => handleRemoveNewImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 dark:bg-red-600 text-white dark:text-white rounded-full hover:bg-red-600 dark:hover:bg-red-700 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              >
                <Upload className="w-4 h-4" />
                <span className={cn(smallTextSize)}>{t('recipes.uploadImages')}</span>
              </button>
            </div>

            {/* Ingredients */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.ingredients')}
              </label>

              {/* Bulk Ingredients Input (only for automation users) */}
              {hasRecipeAutomationAccess && (
                <div className="mb-4 p-4 border border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/20">
                  <label className={cn('text-xs font-medium', secondaryText, 'block mb-2')}>
                    Quick Entry (Format: name: amount)
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={bulkIngredientsText}
                      onChange={(e) => setBulkIngredientsText(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark text-sm min-h-[100px] font-mono"
                      placeholder="Example:&#10;Flour: 500g&#10;Sugar: 200g&#10;Salt"
                    />
                    <button
                      onClick={handleParseBulkIngredients}
                      disabled={!bulkIngredientsText.trim()}
                      className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white dark:text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed self-start"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                  </div>
                  <p className={cn('text-xs mt-2', secondaryText)}>
                    Enter one ingredient per line. Format: "name: amount" or just "name"
                  </p>
                </div>
              )}

              {/* Ingredient Input Form */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={ingredientName}
                  onChange={(e) => setIngredientName(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark text-sm"
                  placeholder={t('recipes.ingredientName')}
                />
                <input
                  type="text"
                  value={ingredientAmount}
                  onChange={(e) => setIngredientAmount(e.target.value)}
                  className="w-32 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark text-sm"
                  placeholder={t('recipes.amount')}
                />
                <button
                  onClick={handleAddIngredient}
                  className="px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-200 transition text-sm font-medium"
                >
                  <Plus className="w-4 h-4 mx-auto" />
                </button>
              </div>

              {/* Ingredients List — inline editable per language */}
              {ingredients.length > 0 && (
                <div className="space-y-2">
                  {ingredients.map((ingredient, index) => (
                    <div
                      key={ingredient.id || index}
                      className="flex items-center gap-2 p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg"
                    >
                      <input
                        type="text"
                        value={getTranslatedText(ingredient.nameTranslations, lang)}
                        onChange={(e) => updateIngredientField(index, 'nameTranslations', e.target.value)}
                        className={cn(
                          'flex-1 bg-transparent dark:bg-transparent border-b border-transparent focus:border-zinc-400 dark:focus:border-zinc-500 outline-none',
                          smallTextSize, primaryText
                        )}
                        placeholder={t('recipes.ingredientName')}
                      />
                      <input
                        type="text"
                        value={ingredient.amountTranslations ? getTranslatedText(ingredient.amountTranslations, lang) : ''}
                        onChange={(e) => updateIngredientField(index, 'amountTranslations', e.target.value)}
                        className={cn(
                          'w-28 bg-transparent dark:bg-transparent border-b border-transparent focus:border-zinc-400 dark:focus:border-zinc-500 outline-none text-right',
                          smallTextSize, secondaryText
                        )}
                        placeholder={t('recipes.amount')}
                      />
                      <button
                        onClick={() => handleRemoveIngredient(index)}
                        className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Steps */}
            <div>
              <label className={cn(smallTextSize, secondaryText, 'block mb-2')}>
                {t('recipes.steps')}
              </label>

              {/* Bulk Steps Input (only for automation users) */}
              {hasRecipeAutomationAccess && (
                <div className="mb-4 p-4 border border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/20">
                  <label className={cn('text-xs font-medium', secondaryText, 'block mb-2')}>
                    Quick Entry (Format: · step description)
                  </label>
                  <div className="flex gap-2">
                    <textarea
                      value={bulkStepsText}
                      onChange={(e) => setBulkStepsText(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark text-sm min-h-[100px] font-mono"
                      placeholder="Example:&#10;· Preheat oven to 180°C&#10;· Mix dry ingredients&#10;Fold in wet ingredients"
                    />
                    <button
                      onClick={handleParseBulkSteps}
                      disabled={!bulkStepsText.trim()}
                      className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white dark:text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed self-start"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                  </div>
                  <p className={cn('text-xs mt-2', secondaryText)}>
                    Enter one step per line. Bullet points (·, •, -) are optional
                  </p>
                </div>
              )}

              {/* Step Input Form */}
              <div className="space-y-2 mb-3">
                <input
                  type="text"
                  value={stepTitle}
                  onChange={(e) => setStepTitle(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark text-sm"
                  placeholder={t('recipes.stepTitle') + ' (' + t('common.optional') + ')'}
                />
                <div className="flex gap-2">
                  <textarea
                    value={stepDescription}
                    onChange={(e) => setStepDescription(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-background dark:bg-background-dark text-text dark:text-text-dark text-sm min-h-[80px]"
                    placeholder={t('recipes.stepDescription')}
                  />
                  <button
                    onClick={handleAddStep}
                    className="px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-200 transition text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Steps List — inline editable per language */}
              {steps.length > 0 && (
                <SortableList
                  items={steps}
                  getId={(step) => step.id || `step-${steps.indexOf(step)}`}
                  onReorder={handleStepsReorder}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  <div className="space-y-2">
                    {steps.map((step, index) => (
                      <SortableStepRow
                        key={step.id || index}
                        step={step}
                        index={index}
                        lang={lang}
                        t={t}
                        onTitleChange={(value) => updateStepField(index, 'titleTranslations', value)}
                        onDescriptionChange={(value) => updateStepField(index, 'descriptionTranslations', value)}
                        onRemove={() => handleRemoveStep(index)}
                      />
                    ))}
                  </div>
                </SortableList>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!(nameTranslations[lang] || '').trim()}
              className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50"
            >
              {mode === 'create' ? t('common.create') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
